/**
 * CommandPalette — ⌘K / Ctrl+K quick search (ClickUp's palette, 2026-09
 * redesign). One field, results grouped by kind — pages, cameras, workers,
 * recent alerts, actions — arrow keys to move, Enter to go, Esc to close.
 *
 * Everything it searches is already in the React Query cache (cameras,
 * workers, the dashboard's recent alerts), so it adds no endpoint and costs
 * no request when opened on a page that has loaded them.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCameras } from "@/hooks/useCameras";
import { useWorkers } from "@/hooks/useWorkers";
import { useDashboard } from "@/hooks/useDashboard";
import { alertTypeLabel } from "@/lib/alertTypeLabel";
import { fmtRelative } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/hooks/useDir";
import { cameraDisplayName } from "@/components/cameras/cameraName";
import {
  IconAlerts,
  IconCameras,
  IconSearch,
  IconWorkers,
  IconZones,
  IconDownload,
  IconPlus,
  NAV_ICON_MAP,
} from "@/components/common/icons";
import { NAV_GROUPS } from "./nav";

type Kind = "pages" | "cameras" | "workers" | "alerts" | "actions";

interface Result {
  id: string;
  kind: Kind;
  label: string;
  meta?: string;
  icon: ReactNode;
  to: string;
}

const MAX_PER_GROUP = 5;

function norm(s: string): string {
  // Strip Hebrew points (niqqud/cantillation, U+0591–U+05C7) so a query matches with or without them.
  return s.toLocaleLowerCase().normalize("NFKD").replace(/[\u0591-\u05C7]/g, "");
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useAuth((s) => s.user?.role === "admin");
  const lang = useLang();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameras = useCameras();
  const workers = useWorkers();
  const dash = useDashboard();

  useEffect(() => {
    if (open) {
      setQ("");
      setCursor(0);
      // After the dialog paints, so the focus sticks.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const all = useMemo<Result[]>(() => {
    const out: Result[] = [];
    for (const g of NAV_GROUPS) {
      for (const item of g.items) {
        if (item.adminOnly && !isAdmin) continue;
        const Icon = NAV_ICON_MAP[item.key];
        out.push({
          id: `page:${item.to}`,
          kind: "pages",
          label: t(`nav.${item.key}`),
          icon: Icon ? <Icon className="w-[18px] h-[18px]" /> : null,
          to: item.to,
        });
      }
    }
    for (const c of cameras.data ?? []) {
      out.push({
        id: `cam:${c.id}`,
        kind: "cameras",
        label: cameraDisplayName(c, lang),
        meta: t("shell.palette.openLive"),
        icon: <IconCameras className="w-[18px] h-[18px]" />,
        to: `/monitor?camera=${encodeURIComponent(c.id)}`,
      });
    }
    for (const w of workers.data ?? []) {
      out.push({
        id: `worker:${w.id}`,
        kind: "workers",
        label: w.name,
        meta: w.role ?? undefined,
        icon: <IconWorkers className="w-[18px] h-[18px]" />,
        to: "/workers",
      });
    }
    for (const a of dash.data?.recent_alerts ?? []) {
      out.push({
        id: `alert:${a.id}`,
        kind: "alerts",
        label: [alertTypeLabel(a.type, t), a.worker_name].filter(Boolean).join(" · "),
        meta: fmtRelative(a.timestamp),
        icon: <IconAlerts className="w-[18px] h-[18px]" />,
        to: `/alerts?alert=${encodeURIComponent(a.id)}`,
      });
    }
    out.push(
      {
        id: "act:export",
        kind: "actions",
        label: t("shell.palette.exportInsurer"),
        icon: <IconDownload className="w-[18px] h-[18px]" />,
        to: "/ai-inspector?tab=safety",
      },
      {
        id: "act:zone",
        kind: "actions",
        label: t("shell.palette.drawZone"),
        icon: <IconZones className="w-[18px] h-[18px]" />,
        to: "/zones",
      },
      {
        id: "act:camera",
        kind: "actions",
        label: t("shell.palette.addCamera"),
        icon: <IconPlus className="w-[18px] h-[18px]" />,
        to: "/cameras",
      },
    );
    return out;
  }, [t, isAdmin, lang, cameras.data, workers.data, dash.data]);

  const groups = useMemo(() => {
    const needle = norm(q.trim());
    const hits = needle
      ? all.filter((r) => norm(r.label).includes(needle) || (r.meta && norm(r.meta).includes(needle)))
      : all.filter((r) => r.kind === "pages" || r.kind === "actions" || r.kind === "alerts");
    const order: Kind[] = ["pages", "cameras", "workers", "alerts", "actions"];
    return order
      .map((kind) => ({ kind, items: hits.filter((r) => r.kind === kind).slice(0, MAX_PER_GROUP) }))
      .filter((g) => g.items.length > 0);
  }, [all, q]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    if (cursor >= flat.length) setCursor(Math.max(0, flat.length - 1));
  }, [flat.length, cursor]);

  if (!open) return null;

  const go = (r: Result | undefined) => {
    if (!r) return;
    onClose();
    navigate(r.to);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(flat.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[cursor]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  let index = -1;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t("shell.search")}>
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative mx-auto mt-[12vh] w-[min(640px,calc(100%-2rem))] bg-white rounded-xl shadow-pop overflow-hidden">
        <div className="flex items-center gap-3 px-4 h-14 border-b border-slate-200">
          <IconSearch className="w-5 h-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKey}
            placeholder={t("shell.searchPlaceholder")}
            aria-label={t("shell.search")}
            className="flex-1 min-w-0 h-full bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          <kbd className="text-[11px] font-medium text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">Esc</kbd>
        </div>
        <div className="max-h-[min(60vh,440px)] overflow-y-auto py-2" role="listbox">
          {groups.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-slate-500">{t("shell.palette.noResults")}</p>
          )}
          {groups.map((g) => (
            <div key={g.kind} className="pb-1">
              <div className="px-4 pt-2 pb-1 text-xs font-semibold text-slate-500">{t(`shell.palette.groups.${g.kind}`)}</div>
              {g.items.map((r) => {
                index += 1;
                const active = index === cursor;
                const myIndex = index;
                return (
                  <button
                    key={r.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setCursor(myIndex)}
                    onClick={() => go(r)}
                    className={`w-full flex items-center gap-3 px-4 h-10 text-start text-sm ${
                      active ? "bg-brand-50 text-brand-700" : "text-slate-700"
                    }`}
                  >
                    <span className={active ? "text-brand-600" : "text-slate-400"}>{r.icon}</span>
                    <span className="flex-1 min-w-0 truncate font-medium">{r.label}</span>
                    {r.meta && <span className="shrink-0 text-xs text-slate-500">{r.meta}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 px-4 h-10 border-t border-slate-200 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <kbd className="border border-slate-200 rounded px-1">↑</kbd>
            <kbd className="border border-slate-200 rounded px-1">↓</kbd>
            {t("shell.palette.move")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="border border-slate-200 rounded px-1">↵</kbd>
            {t("shell.palette.open")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Global ⌘K / Ctrl+K binding. */
export function useCommandPaletteHotkey(onOpen: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);
}
